import type { WithStyles } from '@material-ui/core';

import { Button, withStyles } from '@material-ui/core';
import { Button as ZincButton } from '@zinc/ui';
import type React from 'react';
import { useEffect, useState } from 'react';
import Secure from 'tm3-core/controls/security/secure';
import colours from '../../../styles/colours';
import styles from './styles';

export interface BzButtonProps extends WithStyles<typeof styles>, React.PropsWithChildren {
  className?: string | null;
  id?: string;
  style?: React.CSSProperties;

  action?: string;
  backgroundColor?: string;
  color?: 'inherit' | 'default' | 'primary' | 'secondary' | 'critical';
  dataTestId?: string | number;
  disabled?: boolean;
  errored?: boolean;
  errorText?: string | null;
  flat?: boolean;
  iconButton?: boolean;
  label?: string | null;
  small?: boolean;
  value?: string;
  variant?: 'solid' | 'outline' | 'ghost' | undefined;
  warning?: boolean;
  skipDebounce?: boolean;
  forceStyle?: boolean;

  onClick?: (e: React.MouseEvent<HTMLElement>) => void;
}

const BzButton: React.FC<BzButtonProps> = ({
  action,
  backgroundColor,
  color,
  children,
  classes,
  className,
  dataTestId,
  disabled,
  errored,
  errorText,
  flat,
  iconButton,
  forceClassName,
  forceStyle = false,
  variant,
  id,
  label,
  small,
  style,
  value,
  warning,
  onClick,
  skipDebounce = false
}) => {
  const [justClicked, setJustClicked] = useState(false);
  const [timeout, setTimeoutRef] = useState<number | undefined>();

  useEffect(() => {
    return () => {
      clearTimeout(timeout);
    };
  }, [timeout]);

  const handleClick = (e: React.MouseEvent<HTMLElement>): void => {
    e.preventDefault();
    if (skipDebounce) {
      onClick?.(e);
      return;
    }

    if (justClicked) {
      if (!timeout) {
        createTimeout(); // just in case
      }
      return; // protect from hammering the click
    }
    setJustClicked(true);
    onClick?.(e);
    createTimeout();
  };

  const createTimeout = (): void => {
    setTimeoutRef(
      window.setTimeout(() => {
        setJustClicked(false);
      }, 1500)
    );
  };

  const overrideStyle = { ...style };
  const hasError = errorText != null && errorText.trim() !== '';
  className = `bz-button${className ? ` ${className}` : ''}`;
  let newVariant = variant ?? 'solid',
    newSize = 'medium',
    newColor = color === 'critical' ? 'critical' : 'primary';

  let root = classes.base;
  if (flat) {
    root = classes.flat;
    newVariant = 'outline';
    newColor = 'neutral';
  }

  if (iconButton) {
    root += ` ${classes.iconButton}`;
  }

  if (backgroundColor === 'white') {
    root += ` ${classes.outlineMode}`;
    newVariant = 'outline';
    newColor = 'neutral';
  }

  if (errored || hasError) {
    root += ` ${classes.errored}`;
    newColor = 'critical';
  }

  if (warning && value) {
    root += ` ${classes.warning}`;
    newColor = 'positive';
  }

  if (small) {
    root += ` ${classes.small}`;
    newSize = 'small';
  }

  if (backgroundColor && backgroundColor !== 'white') {
    // hack below for compatibility
    if (!overrideStyle.backgroundColor && !overrideStyle.background) {
      overrideStyle.backgroundColor = backgroundColor;
    }
  }

  if (disabled) {
    root += ` ${classes.disabled}`;
    overrideStyle.background = undefined;
    overrideStyle.backgroundColor = colours.grey50;
  }

  if (forceStyle) forceStyle = Object.keys(overrideStyle).length > 0;

  const content = (
    <ZincButton
      variant={newVariant}
      color={newColor}
      size={newSize}
      onClick={handleClick}
      {...{ disabled, id }}
      className={forceClassName && className}
      attributes={{
        style: (forceStyle && overrideStyle) || undefined,
        'data-testid': dataTestId
      }}
    >
      {children || label}
    </ZincButton>
  );

  if (action) {
    return <Secure rules={[action]}>{content}</Secure>;
  }

  return content;
};

export default withStyles(styles)(BzButton);
